/**
 * Returns a numeric sort score representing how closely `label` matches `query`.
 * Lower = stronger match:
 *   0           – exact match (case-insensitive)
 *   1           – label starts with query
 *   2 + idx     – label contains query at character position idx
 *   Infinity    – no match
 */
export function matchScore(label: string, query: string): number {
  if (!query) {
    return 0;
  }
  const lowerLabel = label.toLowerCase();
  const lowerQuery = query.toLowerCase();
  if (lowerLabel === lowerQuery) {
    return 0;
  }
  if (lowerLabel.startsWith(lowerQuery)) {
    return 1;
  }
  const idx = lowerLabel.indexOf(lowerQuery);
  return idx === -1 ? Infinity : 2 + idx;
}

interface MatchableOption {
  value: string | number | boolean;
  displayName?: string;
}

function optionMatchLabel(option: MatchableOption): string {
  return option.displayName ?? String(option.value);
}

export function optionMatchScore(option: MatchableOption, query: string): number {
  return Math.min(matchScore(String(option.value), query), matchScore(option.displayName ?? '', query));
}

export function compareOptionMatch(a: MatchableOption, b: MatchableOption, query: string): number {
  const scoreDiff = optionMatchScore(a, query) - optionMatchScore(b, query);
  if (scoreDiff !== 0) {
    return scoreDiff;
  }
  return optionMatchLabel(a).length - optionMatchLabel(b).length;
}

export function bestMatchIndex<T extends MatchableOption>(
  options: readonly T[],
  query: string,
  isCandidate: (option: T) => boolean = () => true,
): number {
  let bestIndex = -1;
  let bestScore = Infinity;
  options.forEach((option, index) => {
    if (!isCandidate(option)) {
      return;
    }
    const score = optionMatchScore(option, query);
    if (score < bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });
  return bestIndex;
}
